import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store/store';
import api from '../services/api';
import { VehicleType, UserRole } from '../types';
import { VehicleTypesDetailModal } from '../components/VehicleTypesDetailModal';

export const VehicleTypesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', licensePlate: '', isActive: true });

  // Detail modal state
  const [selectedVehicleType, setSelectedVehicleType] = useState<VehicleType | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const canManage = user?.role === UserRole.MANAGER ||
                    user?.role === UserRole.ORG_ADMIN ||
                    user?.role === UserRole.PLATFORM_ADMIN ||
                    user?.role === UserRole.PROVIDER_MANAGER ||
                    user?.role === UserRole.PROVIDER_USER;

  useEffect(() => {
    fetchVehicleTypes();
  }, []);

  const fetchVehicleTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vehicle-types');
      setVehicleTypes(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || t('vehicleTypes.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.patch(`/vehicle-types/${editingId}`, formData);
      } else {
        // When creating, send name and licensePlate (isActive defaults to true on backend)
        await api.post('/vehicle-types', { name: formData.name, licensePlate: formData.licensePlate || undefined });
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', licensePlate: '', isActive: true });
      fetchVehicleTypes();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || t('vehicleTypes.failedToSave'));
    }
  };

  const handleRowClick = (vehicleType: VehicleType) => {
    setSelectedVehicleType(vehicleType);
    setIsDetailModalOpen(true);
  };

  const handleEdit = (vehicleType?: VehicleType) => {
    const vt = vehicleType || selectedVehicleType;
    if (vt) {
      setEditingId(vt.id);
      setFormData({ name: vt.name, licensePlate: vt.licensePlate || '', isActive: vt.isActive });
      setShowForm(true);
      setIsDetailModalOpen(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', licensePlate: '', isActive: true });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('vehicleTypes.title')}</h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
            {t('vehicleTypes.subtitle')}
          </p>
        </div>
        {canManage && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-base font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors min-h-[44px]"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('vehicleTypes.addVehicleType')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {showForm && canManage && (
        <div className="mb-6 bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingId ? t('vehicleTypes.editVehicleType') : t('vehicleTypes.addNewVehicleType')}
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="px-6 py-6">
            <div className="mb-6">
              <label htmlFor="name" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('vehicleTypes.vehicleTypeName')} *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder={t('vehicleTypes.vehicleTypeNamePlaceholder')}
                className="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="licensePlate" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('vehicleTypes.licensePlate')} *
              </label>
              <input
                type="text"
                id="licensePlate"
                value={formData.licensePlate}
                onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                required
                placeholder={t('vehicleTypes.licensePlatePlaceholder')}
                className="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Only show Active checkbox when editing */}
            {editingId && (
              <div className="mb-6 flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-3 block text-base text-gray-700 dark:text-gray-300">
                  {t('vehicleTypes.activeHint')}
                </label>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors min-h-[44px]"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-blue-600 text-white text-base font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors min-h-[44px]"
              >
                {editingId ? t('common.update') : t('common.add')}
              </button>
            </div>
          </form>
        </div>
      )}

      {vehicleTypes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <svg className="mx-auto h-12 w-12 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('vehicleTypes.noVehicleTypes')}</p>
          {canManage && (
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">{t('vehicleTypes.addVehicleTypeHint')}</p>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {vehicleTypes.map((vehicleType) => (
              <li key={vehicleType.id}>
                <div
                  onClick={() => handleRowClick(vehicleType)}
                  className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="text-base sm:text-lg font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          {vehicleType.name}
                        </h3>
                        {!vehicleType.isActive && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {t('common.inactive')}
                          </span>
                        )}
                      </div>
                      {vehicleType.licensePlate && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">{t('vehicleTypes.licensePlate')}:</span> {vehicleType.licensePlate}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Detail Modal */}
      <VehicleTypesDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        vehicleType={selectedVehicleType}
        onEdit={() => handleEdit()}
        canManage={canManage}
      />
    </div>
  );
};
